import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Error getting session:', error);
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Check for pending redirect after OAuth
      if (session?.user && window.location.hash.includes('access_token')) {
        // Clean up the URL hash and redirect
        const redirectPath = sessionStorage.getItem('auth_redirect') || '/';
        sessionStorage.removeItem('auth_redirect');
        window.history.replaceState(null, '', redirectPath);
        window.location.href = redirectPath;
      } else if (session?.user) {
        // For non-OAuth, check for stored redirect
        const redirectPath = sessionStorage.getItem('auth_redirect');
        if (redirectPath) {
          sessionStorage.removeItem('auth_redirect');
          window.location.href = redirectPath;
        }
      }
    }).catch((err) => {
      console.error('Supabase connection error:', err);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state change:', _event, session?.user?.email);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Check for pending redirect after OAuth or email confirmation
      if ((_event === 'SIGNED_IN' || _event === 'TOKEN_REFRESHED') && session?.user) {
        // Clean up the URL hash if it contains auth tokens
        if (window.location.hash.includes('access_token')) {
          // Get the stored redirect path or default to homepage
          const redirectPath = sessionStorage.getItem('auth_redirect') || '/';
          sessionStorage.removeItem('auth_redirect');
          
          // Use router navigation or window.location
          window.history.replaceState(null, '', redirectPath);
          window.location.href = redirectPath;
        } else {
          // For non-OAuth logins, check for stored redirect
          const redirectPath = sessionStorage.getItem('auth_redirect');
          if (redirectPath) {
            sessionStorage.removeItem('auth_redirect');
            window.location.href = redirectPath;
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const getAppOrigin = () =>
    typeof window !== 'undefined' ? window.location.origin : undefined;

  const signInWithGoogle = async (redirectPath = null) => {
    // Store the redirect path in sessionStorage before OAuth
    if (redirectPath) {
      sessionStorage.setItem('auth_redirect', redirectPath);
    }

    // Use current origin (handles localhost with any port, and production domain)
    const redirectUrl = getAppOrigin();

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: redirectUrl ? { redirectTo: redirectUrl } : {},
    });
    return { data, error };
  };

  const signInWithPassword = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  };

  const signUpWithPassword = async (email, password, options = {}) => {
    const origin = getAppOrigin();
    const redirectPath = options.redirectTo ?? '/auth/confirm';
    const emailRedirectTo = origin
      ? `${origin}${redirectPath}`
      : undefined;

    const signUpOptions = {
      ...options,
      emailRedirectTo,
    };
    delete signUpOptions.redirectTo;
    if (!emailRedirectTo) {
      delete signUpOptions.emailRedirectTo;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: signUpOptions,
    });
    return { data, error };
  };

  const resetPasswordForEmail = async (email, options = {}) => {
    const origin = getAppOrigin();
    const redirectPath = options.redirectTo ?? '/auth/confirm?next=/account';
    const redirectTo = origin ? `${origin}${redirectPath}` : undefined;

    const resetOptions = {
      ...options,
      redirectTo,
    };
    delete resetOptions.redirectTo;
    if (!redirectTo) {
      delete resetOptions.redirectTo;
    }

    const { data, error } = await supabase.auth.resetPasswordForEmail(email, resetOptions);
    return { data, error };
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setSession(null);
    }
    return { error };
  };

  const updateUser = async (userData) => {
    const { data, error } = await supabase.auth.updateUser({
      data: userData
    });
    if (!error) {
      setUser(data.user);
    }
    return { data, error };
  };

  const value = {
    user,
    session,
    login: signInWithPassword, // Now points to email/password login
    signInWithGoogle,
    signInWithPassword,
    signUpWithPassword,
    resetPasswordForEmail,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
